import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Card, CardBody, Container } from "reactstrap";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";
import { toast } from "react-toastify";

import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import CandidateOnboardingTableContainer from "components/Common/CandidateOnboardingTableContainer";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import CandidateHireStatusAddEditModal from "pages/AllCommonModal/CandidateHireStatusAddEditModal/candidate-hire-status-add-edit-modal";
import {
  getHireStatusMetadata,
  submitHireStatusFilterData,
} from "model/CandidateModel";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import {
  buildEmptyFilterFromMeta,
  countActiveMetadataFilters,
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  isMetadataFilterDefault,
  mapFilterDefinitionsToModalData,
  mergeModalSubmitIntoListFilter,
  normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const strOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const normalizeHireFilterPayload = (raw = {}, filterMeta = []) => {
  const out = { ...raw };
  const pageNumber = Number(out.pageNumber) || 1;
  const pageSize = Number(out.pageSize) || DEFAULT_PAGE_SIZE;
  const dateFields = new Set(
    filterMeta.filter((f) => f?.type === "date" && f?.field).map((f) => f.field)
  );

  const next = { pageNumber, pageSize };
  Object.keys(out).forEach((key) => {
    if (key === "pageNumber" || key === "pageSize") return;
    let v = out[key];
    if (v === "" || v === undefined) {
      next[key] = null;
      return;
    }
    if (dateFields.has(key)) {
      next[key] = normalizeMetadataFilterDateForApi(v);
      return;
    }
    if (typeof v === "string") {
      next[key] = strOrNull(v);
      return;
    }
    next[key] = v;
  });
  Object.keys(next).forEach((payloadKey) => {
    if (isAsyncFilterLabelFieldKey(payloadKey)) {
      delete next[payloadKey];
    }
  });
  return next;
};

const CandidateHireStatus = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchMetadataSchema, getDropdownOptionsByApiKey } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("candidate-hire-status");
  const initialFilterFetchedRef = useRef(false);
  const [metadataBootstrapDone, setMetadataBootstrapDone] = useState(false);
  const [hireListFilter, setHireListFilter] = useState({});
  const [hireData, setHireData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [tableMetaData, setTableMetaData] = useState([]);
  const [hireFilterMetaData, setHireFilterMetaData] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const hireStatusActionPermissions = useMemo(
    () => ({
      canEdit: Boolean(permissionData?.CandidateHireStatusEditAccess),
    }),
    [permissionData]
  );

  const hasAnyHireStatusRowAction = useMemo(
    () => hireStatusActionPermissions.canEdit,
    [hireStatusActionPermissions]
  );

  /** Remote/static options for metadata `dropdown` fields in the upper toolbar (same pattern as all-candidates.jsx). */
  const [toolbarDropdownReactSelectOptionsByFieldName, setToolbarDropdownReactSelectOptionsByFieldName] =
    useState({});

  const defaultTableColumns = useMemo(
    () => [
      { Header: "Client", accessor: "client" },
      { Header: "Name", accessor: "name" },
      { Header: "Date Hired", accessor: "dateHired" },
      { Header: "Status", accessor: "status" },
    ],
    []
  );

  /** Metadata `isDefault: true` → upper toolbar beside the table (same rules as All Candidates). */
  const hireMetadataToolbarFilterDefinitions = useMemo(
    () =>
      (Array.isArray(hireFilterMetaData) ? hireFilterMetaData : []).filter(
        (filterDefinitionRow) =>
          filterDefinitionRow?.field && isMetadataFilterDefault(filterDefinitionRow)
      ),
    [hireFilterMetaData]
  );

  /** Not default (or omitted) → AllCommonFilterModal only. */
  const hireMetadataModalOnlyFilterDefinitions = useMemo(
    () =>
      (Array.isArray(hireFilterMetaData) ? hireFilterMetaData : []).filter(
        (filterDefinitionRow) =>
          filterDefinitionRow?.field && !isMetadataFilterDefault(filterDefinitionRow)
      ),
    [hireFilterMetaData]
  );

  const usesMetadataDrivenHireFilterUi =
    Array.isArray(hireFilterMetaData) && hireFilterMetaData.length > 0;

  /** FILTERS / RESET hidden when modal would be empty because every metadata field is a default-toolbar field. */
  const shouldShowTableHeaderFilterAndResetControls = useMemo(
    () =>
      !usesMetadataDrivenHireFilterUi ||
      hireMetadataModalOnlyFilterDefinitions.length > 0,
    [usesMetadataDrivenHireFilterUi, hireMetadataModalOnlyFilterDefinitions]
  );

  const activeModalFilterCount = useMemo(
    () =>
      countActiveMetadataFilters(hireListFilter, hireMetadataModalOnlyFilterDefinitions),
    [hireListFilter, hireMetadataModalOnlyFilterDefinitions]
  );

  const shouldRenderHireMetadataToolbarFilterRow =
    hireMetadataToolbarFilterDefinitions.length > 0;

  const buildBaselineEmptyHireListFilterFromMetadata = useCallback(
    () => buildEmptyFilterFromMeta(hireFilterMetaData),
    [hireFilterMetaData]
  );

  /** Modal slice: metadata entries that are not `isDefault: true`. */
  const hireStatusFilterModalData = useMemo(() => {
    if (hireMetadataModalOnlyFilterDefinitions.length) {
      return mapFilterDefinitionsToModalData(
        hireMetadataModalOnlyFilterDefinitions,
        hireListFilter
      );
    }

    return [];
  }, [hireListFilter, hireMetadataModalOnlyFilterDefinitions]);

  /** Ensure async select label companion keys exist once metadata resolves. */
  useEffect(() => {
    if (!Array.isArray(hireFilterMetaData) || !hireFilterMetaData.length) return;
    setHireListFilter((previousHireFilter) => {
      const mergedHireFilter = { ...previousHireFilter };
      let mergedNewKeysIntoState = false;
      hireFilterMetaData.forEach((filterDefinitionRow) => {
        const backendFilterFieldIdentifier = filterDefinitionRow?.field;
        if (!backendFilterFieldIdentifier) return;
        if (!(backendFilterFieldIdentifier in mergedHireFilter)) {
          mergedHireFilter[backendFilterFieldIdentifier] =
            filterDefinitionRow?.type === "date" ? null : "";
          mergedNewKeysIntoState = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionRow)) {
          const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(backendFilterFieldIdentifier);
          if (!(asyncLabelFieldKey in mergedHireFilter)) {
            mergedHireFilter[asyncLabelFieldKey] = "";
            mergedNewKeysIntoState = true;
          }
        }
      });
      return mergedNewKeysIntoState ? mergedHireFilter : previousHireFilter;
    });
  }, [hireFilterMetaData]);

  useEffect(() => {
    const toolbarDropdownDefinitions = hireMetadataToolbarFilterDefinitions.filter(
      (definitionRow) =>
        definitionRow?.type === "dropdown" && definitionRow?.field
    );
    if (!toolbarDropdownDefinitions.length) {
      setToolbarDropdownReactSelectOptionsByFieldName({});
      return;
    }
    let cancelled = false;
    (async () => {
      const tuples = await Promise.all(
        toolbarDropdownDefinitions.map(async (definitionRow) => {
          const fieldKey = definitionRow.field;
          const staticOptions =
            Array.isArray(definitionRow?.itemArray) ? definitionRow.itemArray : [];
          if (staticOptions.length) return [fieldKey, staticOptions];
          const fetched = await getDropdownOptionsByApiKey(definitionRow?.api);
          return [fieldKey, fetched];
        })
      );
      if (cancelled) return;
      const byField = {};
      tuples.forEach(([fieldKey, options]) => {
        byField[fieldKey] = options;
      });
      setToolbarDropdownReactSelectOptionsByFieldName(byField);
    })();
    return () => {
      cancelled = true;
    };
  }, [hireMetadataToolbarFilterDefinitions, getDropdownOptionsByApiKey]);

  const mapRecordsToRows = (list = []) =>
    list.map((item = {}) => ({
      ...item,
      _originalHireStatus: { ...item },
      id: item?.id || item?._id || "",
    }));

  const buildPayloadFromFilterForm = (form = {}, overrides = {}) =>
    normalizeHireFilterPayload(
      {
        pageNumber: 1,
        pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
        ...form,
        ...overrides,
      },
      hireFilterMetaData
    );

  const getHireStatusList = async (payloadOverrides = {}, options = {}) => {
    const { onlyPagination = false, filterMeta: filterMetaOption } = options;
    const filterMeta = filterMetaOption ?? hireFilterMetaData;
    setLoading(true);
    try {
      const payload = onlyPagination
        ? {
            pageNumber: Number(payloadOverrides.pageNumber) || 1,
            pageSize: Number(payloadOverrides.pageSize) || DEFAULT_PAGE_SIZE,
          }
        : normalizeHireFilterPayload(
            {
              pageNumber: requestPayload.pageNumber,
              pageSize: requestPayload.pageSize,
              ...requestPayload,
              ...payloadOverrides,
            },
            filterMeta
          );

      const response = await submitHireStatusFilterData(payload);
      const responseData = response?.data ?? response ?? {};
      const listData =
        responseData?.records ??
        responseData?.data ??
        (Array.isArray(responseData) ? responseData : []);

      const records = Array.isArray(listData) ? listData : [];
      setHireData(mapRecordsToRows(records));

      if (response == null || response?.success !== false) {
        setPaginationMeta({
          pageNumber:
            Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || records.length,
        });
        if (onlyPagination) {
          setRequestPayload({
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
          });
          setHireListFilter(buildEmptyFilterFromMeta(filterMeta));
        } else {
          setRequestPayload(normalizeHireFilterPayload(payload, filterMeta));
        }
      } else {
        setHireData([]);
        if (response) {
          toast.error(response?.message || "Unable to load hire status list");
        }
      }
    } catch (err) {
      toast.error(err?.message || "Unable to load hire status list");
      setHireData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      let filters = [];
      try {
        const metaRes = await fetchMetadataSchema("metadata:hireStatus", () => getHireStatusMetadata());
        const meta = metaRes?.data ?? metaRes ?? {};
        filters = Array.isArray(meta?.filters) ? meta.filters : [];

        const tableColumns = meta?.table?.columns?.length
          ? meta.table.columns.map((col) => {
              const accessor = pascalToCamel(col?.field);
              return {
                Header: col?.title,
                accessor,
                type: col?.type,
              };
            })
          : defaultTableColumns;
        setTableMetaData(tableColumns);
        setHireFilterMetaData(filters);
        setHireListFilter(buildEmptyFilterFromMeta(filters));
      } catch (e) {
        toast.error(e?.message || "Unable to load hire status metadata");
        setTableMetaData(defaultTableColumns);
        setHireFilterMetaData([]);
        setHireListFilter({});
      }

      setMetadataBootstrapDone(true);
    };
    bootstrap();
  }, []);

  /** 1) settings page size ready → 2) call filter API once */
  useEffect(() => {
    if (!settingsReady || !metadataBootstrapDone || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    void getHireStatusList(
      {
        pageNumber: 1,
        pageSize,
        ...buildEmptyFilterFromMeta(hireFilterMetaData),
      },
      { onlyPagination: false, filterMeta: hireFilterMetaData }
    );
  }, [settingsReady, settingsPageSize, metadataBootstrapDone, hireFilterMetaData]);

  /** Apply modal fields only — toolbar/default fields remain (same behaviour as All Candidates). */
  const handleFilterSubmit = async (modalFormPayload = {}) => {
    const mergedHireFilter = mergeModalSubmitIntoListFilter(
      hireListFilter,
      hireStatusFilterModalData,
      modalFormPayload
    );
    setHireListFilter(mergedHireFilter);
    setCommonFilterModalOpen(false);
    const payload = buildPayloadFromFilterForm(mergedHireFilter);
    await getHireStatusList(payload);
  };

  const handleUpperToolbarHireFilterSearch = async () => {
    const payloadFromToolbar = buildPayloadFromFilterForm(hireListFilter);
    await getHireStatusList(payloadFromToolbar);
  };

  const handleToolbarTextFilterInputChange = (backendFieldIdentifier, rawStringValueFromDom) => {
    setHireListFilter((previousHireFilter) => ({
      ...previousHireFilter,
      [backendFieldIdentifier]: rawStringValueFromDom ?? "",
    }));
  };

  const handleToolbarReactSelectFilterChange = (
    backendFieldIdentifier,
    reactSelectSelectedOptionRow
  ) => {
    setHireListFilter((previousHireFilter) => ({
      ...previousHireFilter,
      [backendFieldIdentifier]: reactSelectSelectedOptionRow?.value ?? "",
      [getAsyncFilterLabelFieldKey(backendFieldIdentifier)]:
        reactSelectSelectedOptionRow?.label ?? "",
    }));
  };

  const handleFilterReset = async () => {
    const resetFilter = buildBaselineEmptyHireListFilterFromMetadata();
    setHireListFilter(resetFilter);
    setCommonFilterModalOpen(false);
    await getHireStatusList(
      { pageNumber: 1, pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE },
      { onlyPagination: true }
    );
  };

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleFilterReset();
    };
    run();
  }, [commonResetFilter]);

  const handlePageChange = async (page) => {
    await getHireStatusList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getHireStatusList({ pageNumber: 1, pageSize: rows });
  };

  const handleEdit = (row) => {
    setEditRow(row && typeof row === "object" ? { ...row } : {});
    setEditOpen(true);
  };

  const closeEditModal = () => {
    setEditOpen(false);
    setEditRow(null);
  };

  const resolveHireStatusRowId = (row = {}) =>
    String(
      row?.id ?? row?._id ?? row?._originalHireStatus?.id ?? row?._originalHireStatus?._id ?? ""
    ).trim();

  /** Merge API/form updates into one visible row without re-running the filter POST. */
  const applyHireStatusRowPatch = useCallback((hireStatusId, partialRawUpdate = {}) => {
    const targetId = String(hireStatusId ?? "").trim();
    if (!targetId || !partialRawUpdate || typeof partialRawUpdate !== "object") return;
    setHireData((previousRows) => {
      let rowMatched = false;
      const nextRows = previousRows.map((tableRow) => {
        if (resolveHireStatusRowId(tableRow) !== targetId) return tableRow;
        rowMatched = true;
        const mergedRawHireStatusRecord = {
          ...(tableRow._originalHireStatus || {}),
          ...partialRawUpdate,
          id: partialRawUpdate?.id ?? tableRow._originalHireStatus?.id ?? targetId,
        };
        const [mappedTableRow] = mapRecordsToRows([mergedRawHireStatusRecord]);
        return mappedTableRow;
      });
      return rowMatched ? nextRows : previousRows;
    });
  }, []);

  const handleHireStatusSaveSuccess = useCallback(
    (updatedRawHireStatus) => {
      if (!updatedRawHireStatus || typeof updatedRawHireStatus !== "object") return;
      const targetId = String(updatedRawHireStatus?.id ?? updatedRawHireStatus?._id ?? "").trim();
      if (!targetId) return;
      applyHireStatusRowPatch(targetId, updatedRawHireStatus);
    },
    [applyHireStatusRowPatch]
  );

  const filteredData = useMemo(() => hireData, [hireData]);

  return (
    <div className="page-content candidate-hire-status-page">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="fw-bold mb-0">Candidate Hire Status</h5>
            </div>
            <hr />

            {shouldRenderHireMetadataToolbarFilterRow ? (
              <MetadataFilterToolbar
                toolbarDefinitions={hireMetadataToolbarFilterDefinitions}
                listFilter={hireListFilter}
                onTextChange={handleToolbarTextFilterInputChange}
                onSelectChange={handleToolbarReactSelectFilterChange}
                onSearch={handleUpperToolbarHireFilterSearch}
                onClear={handleFilterReset}
                showClearWhenNoModalFilters={
                  !shouldShowTableHeaderFilterAndResetControls
                }
                toolbarDropdownOptionsByFieldName={
                  toolbarDropdownReactSelectOptionsByFieldName
                }
                layoutPreset="callExecutive"
                pageName="candidate-hire-status"
              />
            ) : null}

            {loading ? (
              // <div
              //   className="d-flex justify-content-center align-items-center py-5 spinner-card-height"              >
              //   <Spinner color="primary" />
                
              // </div>
              <Loaders />
            ) : tableMetaData?.length ? (
              <CandidateOnboardingTableContainer
                columns={tableMetaData}
                data={filteredData}
                isGlobalFilter={false}
                isSort={true}
                isActionRequired={hasAnyHireStatusRowAction}
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light"
                onEdit={
                  hireStatusActionPermissions.canEdit ? handleEdit : undefined
                }
                onHireStatusEdit={
                  hireStatusActionPermissions.canEdit ? handleEdit : undefined
                }
                pageName="candidate-hire-status"
                onHireStatusSaved={handleHireStatusSaveSuccess}
                isPagination={true}
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                setCommonFilterModalOpen={setCommonFilterModalOpen}
                setCommonResetFilter={setCommonResetFilter}
                allCommonFilter={
                  shouldShowTableHeaderFilterAndResetControls &&
                  hireStatusFilterModalData.length > 0
                }
                activeFilterCount={activeModalFilterCount}
                isCheckboxLeftSide={false}
                enableBulkDeactivate={false}
              />
            ) : (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            )}
          </CardBody>
        </Card>

        {hireStatusFilterModalData.length > 0 && (
          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Hire Status Filter"
            handleSubmit={handleFilterSubmit}
            filterData={hireStatusFilterModalData}
            commonResetFilter={commonResetFilter}
            setCommonResetFilter={setCommonResetFilter}
            pageName="candidate-hire-status"
          />
        )}

        <CandidateHireStatusAddEditModal
          key={editOpen ? editRow?.id || editRow?._id || "hire-edit" : "hire-edit-closed"}
          isOpen={editOpen}
          onClose={closeEditModal}
          row={editRow || {}}
          onSuccess={handleHireStatusSaveSuccess}
        />
      </Container>
    </div>
  );
};

CandidateHireStatus.propTypes = {
  orders: PropTypes.array,
};

export default withRouter(CandidateHireStatus);
